import React, { useEffect, useState } from 'react';

import { NButton, NDialog, NInput } from '@innersource-nn/novoui-react';
import { ModalProps } from 'types/Modal';
import useUpdateEnvironment from '../hooks/useUpdateEnvironment';
import NInputWithChips from 'components/Inputs/Labels Input/NInputWithChips';
import NCheckboxTooltip from 'components/Inputs/InputsWithTooltip/NCheckboxTooltip';
import { BlockPreviewOptions, ModifyEnvironmentInput } from 'types/Environment';
import Spinner from 'components/loader/Spinner';
import NTextAreaQuill from 'components/Inputs/TextAreaQuill/NTextAreaQuill';

const EditEnvironmentModal = ({ visible, onClose }: ModalProps) => {
    const { environment, loading, handleSubmit, updating } =
        useUpdateEnvironment();

    const [formData, setFormData] = useState<ModifyEnvironmentInput>({
        description: environment.description,
        SamlGroupName: environment.originSamlGroupName,
        SamlFullDevGroupName: environment.originSamlFullDevGroupName,
        SamlReaderGroupName: environment.originSamlReaderGroupName,
        identityProviderName: environment.identityProviderName,
        blockpreview: environment.blockpreview,
        quicksight_enabled: environment.quicksight_enabled,
        role_overwrite_enabled: environment.role_overwrite_enabled,
    });
    const updateLabels = (inputName: string, newValue: string[]) => {
        setFormData((prevData: any) => {
            return {
                ...prevData,
                [inputName]: newValue,
            };
        });
    };
    const handleClick = async () => {
        await handleSubmit(formData);
        if (onClose) onClose();
    };

    useEffect(() => {
        setFormData({
            description: environment.description,
            SamlGroupName: environment.originSamlGroupName,
            SamlBasicDevGroupName: environment.originSamlBasicDevGroupName,
            SamlFullDevGroupName: environment.originSamlFullDevGroupName,
            SamlReaderGroupName: environment.originSamlReaderGroupName,
            identityProviderName: environment.identityProviderName,
            blockpreview: environment.blockpreview,
            quicksight_enabled: environment.quicksight_enabled,
            role_overwrite_enabled: environment.role_overwrite_enabled,
        });
    }, [environment]);

    return (
        <NDialog
            isVisible={visible}
            closeDialog={onClose}
            backgroundClose
            headline="Edit environment details"
            primaryLabel="Console"
            size="large"
            showDividers={false}
        >
            <div>
                {loading ? (
                    <Spinner />
                ) : (
                    <div className="flex flex-col gap-16 info-container">
                        <NTextAreaQuill
                            label="Environment description"
                            placeholder="Environment description..."
                            variant="outline"
                            modelValue={formData.description}
                            onChange={(value) =>
                                setFormData({
                                    ...formData,
                                    description: value,
                                })
                            }
                        />
                        <div className="grid grid-cols-2 gap-16">
                            <NInputWithChips
                                label="Admin groups"
                                name="SamlGroupName"
                                labels={formData.SamlGroupName ?? []}
                                content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                                placeholder="Type and press enter... "
                                modelValue=""
                                updateLabels={updateLabels}
                            />
                            <NInputWithChips
                                label="Developer groups"
                                name="SamlFullDevGroupName"
                                labels={formData.SamlFullDevGroupName ?? []}
                                content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                                placeholder="Type and press enter... "
                                modelValue=""
                                updateLabels={updateLabels}
                            />
                            <NInputWithChips
                                label="Reader groups"
                                name="SamlReaderGroupName"
                                labels={formData.SamlReaderGroupName ?? []}
                                content="Add descriptive tags or keywords to categorize your datasets using meaningful words."
                                placeholder="Type and press enter... "
                                modelValue=""
                                updateLabels={updateLabels}
                            />
                            <NInput
                                label="Identity Provider Name"
                                variant="outline"
                                placeholder="Identity Provider Name..."
                                modelValue={formData.identityProviderName}
                                className={
                                    formData.SamlReaderGroupName?.length
                                        ? 'mb-32'
                                        : ''
                                }
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        identityProviderName: e.target.value,
                                    })
                                }
                            />
                            <NCheckboxTooltip
                                label="Block preview to all datasets"
                                content="Allows users to glimpse into their environments datasets before requesting for full access to that dataset."
                                name="blockpreview"
                                checkboxSize="medium"
                                checked={
                                    formData.blockpreview ===
                                    BlockPreviewOptions.All
                                }
                                // modelValue={formData.blockpreview}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        blockpreview: e.target.checked
                                            ? BlockPreviewOptions.All
                                            : BlockPreviewOptions.None,
                                    });
                                }}
                            />
                            <NCheckboxTooltip
                                content="Can be enabled to allow Amazon Quicksight to gain access to the tables in your environment."
                                label="Enable quicksight access"
                                name="quicksight_enabled"
                                checkboxSize="medium"
                                checked={formData.quicksight_enabled}
                                onChange={(e) =>
                                    setFormData({
                                        ...formData,
                                        quicksight_enabled: e.target.checked,
                                    })
                                }
                            />
                            <NCheckboxTooltip
                                content="Datahub users may have manually modified the environment roles created by Datahub. By enabling environment role overwriting you are allowing Datahub to overwrite any such manual modifications which allows Datahub to change the permissions of the roles. Datahub only changes the permissions to enable new features or to improve security. To use the AWS Cloud Development Kit (CDK) using Datahub roles you need to enable this option."
                                label="Enable environment role overwriting"
                                secondaryInfo="Once enabled, the option cannot be disabled"
                                name="role_overwrite_enabled"
                                checkboxSize="medium"
                                checked={formData.role_overwrite_enabled}
                                onChange={(e) => {
                                    setFormData({
                                        ...formData,
                                        role_overwrite_enabled:
                                            e.target.checked,
                                    });
                                }}
                                disabled={environment.role_overwrite_enabled}
                            />
                        </div>
                        <div className="flex justify-end">
                            <NButton
                                variant="fill"
                                square
                                isLoading={updating}
                                onClick={handleClick}
                            >
                                Update Environment
                            </NButton>
                        </div>
                    </div>
                )}
            </div>
        </NDialog>
    );
};
export default EditEnvironmentModal;
